/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { log } from "./utility/log";

/**
 * Returns the property value by a given path
 * @example
 * ```ts
 * const object = {
 *  test: {
 *    a: 1,
 *    b: 1
 *  }
 * }
 *
 * getNestedObjectValues(object, "test.a")
 * // 1
 * ```
 * @param object
 * @param path
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export function getNestedObjectValues(object: Object, path: string) {
  const pathBits = path.split(".");

  for (const pathBit of pathBits) {
    object = object[pathBit] ?? "";
  }

  return object;
}

/**
 * Sets the property value by a given path
 * @example
 * ```ts
 * const object = {
 *  test: {
 *    a: 1,
 *    b: 1
 *  }
 * }
 *
 * setNestedObjectValues(object, "test.a", 2)
 * // {
 * // test: {
 * //   a: 2,
 * //   b: 1
 * // }
 * }
 * ```
 * @param object
 * @param path
 * @param value
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export function setNestedObjectValues(object: Object, path: string, value: any) {
  const pathBits = path.split(".");

  if (pathBits.length === 1) {
    object[pathBits[0]] = value;
  } else {
    const firstBit = pathBits.splice(0, 1);
    object[firstBit[0]] = setNestedObjectValues(object[firstBit[0]] ?? {}, pathBits.join("."), value);
  }

  return object;
}

/**
 * Converts class instances into plain JSON-like objects while preserving
 * nested data and getter-backed properties, which become writable properties in the resulting object.
 * @param input The object to convert.
 * @returns A plain object with the same data as the input, but without class instances.
 */
export function toPlainObject<T>(input: T, seen = new Set<object>()): T {
  if (input === null || input === undefined) {
    return input;
  }

  if (typeof input !== "object") {
    return input;
  }

  if (input instanceof Date) {
    return new Date(input.getTime()) as any;
  }

  if (seen.has(input as object)) {
    return undefined as any;
  }

  seen.add(input as object);

  if (Array.isArray(input)) {
    return input.map((item) => toPlainObject(item, seen)) as any;
  }

  const objectInput = input as Record<string, any>;
  const plain: Record<string, any> = {};

  const keys = new Set<string>();
  // Own enumerable properties
  for (const key of Object.keys(objectInput)) {
    keys.add(key);
  }
  // Getter-backed properties on prototype chain (up to but not including Object.prototype)
  let proto = Object.getPrototypeOf(objectInput);
  while (proto && proto !== Object.prototype) {
    for (const key of Object.getOwnPropertyNames(proto)) {
      if (key === "constructor") continue;
      const descriptor = Object.getOwnPropertyDescriptor(proto, key);
      if (descriptor && typeof descriptor.get === "function") {
        keys.add(key);
      }
    }
    proto = Object.getPrototypeOf(proto);
  }

  for (const key of keys) {
    try {
      const value = objectInput[key];
      plain[key] = toPlainObject(value, seen);
    } catch (error) {
      log.error(error);
    }
  }

  return plain as T;
}

/**
 * Given a nested object returns the path in dot notation of the object.
 * @example
 *
 * ```ts
 * const object = {
 *  test: {
 *    a: 1,
 *    b: 1
 *  }
 * }
 *
 * propertiesToArray(object)
 * // ['test.a', 'test.b']
 * ```
 * @param obj to return the property path
 * @returns the path separated by a `.`
 */
export function propertiesToArray(obj: any) {
  const isObject = (val: object) => val && typeof val === "object" && !Array.isArray(val);

  const addDelimiter = (a: string, b: string): string => (a ? `${a}.${b}` : b);

  const getEntries = (obj: Record<string, any>): [string, any][] => {
    const proto = Object.getPrototypeOf(obj);
    const getterKeys =
      proto && proto !== Object.prototype
        ? Object.getOwnPropertyNames(proto).filter((key) => {
            if (key === "constructor") return false;
            const descriptor = Object.getOwnPropertyDescriptor(proto, key);
            return descriptor !== undefined && typeof descriptor.get === "function";
          })
        : [];
    const getterEntries: [string, any][] = getterKeys.map((key) => [key, obj[key]]);
    return [...Object.entries(obj), ...getterEntries];
  };

  const paths = (obj: object = {}, head = ""): string[] => {
    return getEntries(obj).reduce((product, [key, value]) => {
      const fullPath = addDelimiter(head, key);

      return isObject(value) ? product.concat(paths(value, fullPath)) : product.concat(fullPath);
    }, [] as string[]);
  };

  return paths(obj);
}
/**
 * Returns true if the given dot-separated path exists on at least one element
 * in the object (including array traversal). Uses key-presence checks rather
 * than value truthiness, matching MongoDB $exists semantics.
 */
export function existsNestedPath(object: any, path: string): boolean {
  const pathBits = path.split(".");
  return _existsNestedPathSegments(object, pathBits, 0);
}

function _existsNestedPathSegments(object: any, pathBits: string[], depth: number): boolean {
  if (object === undefined || object === null) {
    return false;
  }
  if (Array.isArray(object)) {
    return object.some((item) => _existsNestedPathSegments(item, pathBits, depth));
  }
  const pathBit = pathBits[depth];
  if (!(pathBit in object)) {
    return false;
  }
  if (depth >= pathBits.length - 1) {
    return true;
  }
  return _existsNestedPathSegments(object[pathBit], pathBits, depth + 1);
}


/**
 * Returns the property value by a given path with array support
 * @param object {refs: [{refId: "1"}, {refId: "2"}]}
 * @param path "refs.refId"
 * @returns ["1", "2"]
 */
export function getArrayedNestedPath(object: any, path: string): (string | boolean | number)[] {
  const pathBits = path.split(".");
  return _getArrayedNestedPathSegments(object, pathBits, 0);
}

/**
 * Internal recursive helper that uses a pre-split path array and an index
 * to avoid repeated split/slice/join string allocations.
 */
function _getArrayedNestedPathSegments(object: any, pathBits: string[], depth: number): (string | boolean | number)[] {
  if (object === undefined || object === null) {
    return [];
  }

  const pathBit = pathBits[depth];
  let pathBitValues = object[pathBit];

  const resses: any[] = [];

  if (!Array.isArray(pathBitValues)) {
    pathBitValues = [pathBitValues];
  }

  const isLast = depth >= pathBits.length - 1;

  for (let i = 0; i < pathBitValues.length; i++) {
    const pathBitValue = pathBitValues[i];
    if (!isLast) {
      const sub = _getArrayedNestedPathSegments(pathBitValue, pathBits, depth + 1);
      for (let j = 0; j < sub.length; j++) {
        resses.push(sub[j]);
      }
    } else if (pathBitValue || pathBitValue === false) {
      resses.push(pathBitValue);
    }
  }

  return resses;
}
